import { observer } from 'mobx-react';
import React, { useEffect } from 'react';
import { StaticContext } from 'react-router';
import { Prompt, RouteComponentProps } from 'react-router-dom';
import itemsStore from '../../../ItemsScreen/store/itemsStore';
import NoData from '../../components/NoData/NoData';
import itemBranchesModifierStore from './store/itemBranchesModifierStore';
import ItemBranchesModifiersRow from './components/ItemBranchesModifiersRow';
import ItemBranchesModifiersRowSkeleton from './components/ItemBranchesModifiersRowSkeleton';
import ItemBranchesModifiersTitles from './components/ItemBranchesModifiersTitles';
import ItemBranchesModifiersTitlesSkeleton from './components/ItemBranchesModifiersTitlesSkeleton';
import { localeStore } from 'src/mobx/localesStore';
import Button from 'src/view/components/Controls/ButtonComponent/Button';
import { BUTTON_TITLE } from '~/constants/main';
import { emptyArray } from '~/utils/helpers';
import Pagination from '~/view/components/Controls/PaginationOld/PaginationOld';
import { getPromptProps } from '~/view/components/CustomPrompt/helper';
import ScrollableBlock from '~/view/containers/ScrollableBlock/ScrollableBlock';
import './styles.scss';
import { confirmWithTrans } from '~/utils/modalConfirmations';
import { paginationLightToPagination } from '~/helpers';
import ModifierPicker from '~/view/components/Controls/Pickers/ModifierPicker/ModifierPicker';
import { IPickerOption } from '~/view/components/Controls/Pickers/Picker/types';

type TProps = RouteComponentProps<{ id?: string }, StaticContext, undefined>;

const ItemBranchesModifiers: React.FC<TProps> = observer((props: TProps): JSX.Element | null => {
  useEffect(() => {
    itemBranchesModifierStore.itemId = Number(props.match.params.id ?? 0);
    itemBranchesModifierStore.modifierId = Number(itemsStore.item?.modifiers?.[0]?.id ?? 0);

    itemBranchesModifierStore.fetchData();

    return () => {
      itemBranchesModifierStore.unMount();
    };
  }, []);

  const handleClickPagination = (page: number) => {
    itemBranchesModifierStore.page = page;
    itemBranchesModifierStore.get();
  };

  const handleSelectModifier = (options: IPickerOption[]) => {
    const option = options.find((option) => Number(option.value));

    if (option !== undefined) {
      itemBranchesModifierStore.modifierId = Number(option.value);
      itemBranchesModifierStore.get();
    }
  };

  // All Update related functions
  const update = () => {
    itemBranchesModifierStore.update();
  };

  const getPriceZeroConfirmation = () => {
    itemBranchesModifierStore.validation.price = true;
    getAllConfirmationsBeforeUpdate();
  };

  const getAvailableConfirmation = () => {
    itemBranchesModifierStore.validation.available = true;
    getAllConfirmationsBeforeUpdate();
  };

  const getAllConfirmationsBeforeUpdate = () => {
    if (!itemBranchesModifierStore.validation.price) {
      confirmWithTrans({ title: 'are_you_sure', message: 'keep_price_at_zero_option', onConfirm: getPriceZeroConfirmation });
      return;
    }

    if (!itemBranchesModifierStore.validation.available) {
      confirmWithTrans({ title: 'are_you_sure_available', message: 'keep_status_different', onConfirm: getAvailableConfirmation });
      return;
    }

    update();
  };

  const handleClickSave = () => {
    if (!itemBranchesModifierStore.modifiers) return;

    itemBranchesModifierStore.validate();

    if (!itemBranchesModifierStore.validation.valid) {
      getAllConfirmationsBeforeUpdate();
      return;
    }

    update();
  };

  if (!itemsStore.item?.id) {
    return null;
  }

  return (
    <>
      <div className="d-flex flex-column flex-grow-1 pt-4 min-width-1000">
        <ModifierPicker onSelect={handleSelectModifier} itemId={itemBranchesModifierStore.itemId} modifierId={itemBranchesModifierStore.modifierId} />

        {itemBranchesModifierStore.loading ? (
          <ItemBranchesModifiersTitlesSkeleton />
        ) : itemBranchesModifierStore.isHasData ? (
          <ItemBranchesModifiersTitles />
        ) : null}

        <ScrollableBlock showScrollbar scrollClassName="d-flex flex-column full-height pr-4" className="mb-3">
          {itemBranchesModifierStore.loading ? (
            emptyArray().map((_, index) => <ItemBranchesModifiersRowSkeleton key={'skeleton-' + String(index)} />)
          ) : itemBranchesModifierStore.isHasData ? (
            itemBranchesModifierStore.modifiers?.map((modifier, index) => <ItemBranchesModifiersRow key={index} modifier={modifier} />)
          ) : (
            <NoData message="no_item_branch_modifiers_data" />
          )}
        </ScrollableBlock>
      </div>

      <Button
        title={localeStore.t(BUTTON_TITLE.SAVE)}
        onClick={handleClickSave}
        disabled={itemBranchesModifierStore.modifierId === 0}
        loading={itemBranchesModifierStore.loading && itemBranchesModifierStore.modifierId > 0}
      />

      <Pagination
        pagination={paginationLightToPagination(itemBranchesModifierStore.pagination, itemBranchesModifierStore.modifiers ?? [])}
        // loading={itemBranchesModifierStore.loading ? LoadingStatus.Loading : LoadingStatus.Success}
        className="mt-2"
        onChange={handleClickPagination}
      />

      {/* <LoadingOverlay visible={itemsStore.isLoading} /> */}
      <Prompt when={itemBranchesModifierStore.isEdited} message={getPromptProps()} />
    </>
  );
});

export default ItemBranchesModifiers;
